<!-- Dashboard View -->
<ngx-spinner color="#cd0005"></ngx-spinner>

<div *ngIf="loaded" id="dashboard-view" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
  <div class="mb-8">
    <h1 class="text-2xl font-bold text-gray-900">Welcome to your Agency Dashboard</h1>
    <p class="mt-1 text-gray-600">Manage your travel agency account and access key features</p>
  </div>
  <!-- Agency Information Cards -->
  <div class="grid grid-cols-1 gap-6 mb-10">
    <!-- First Row -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
      <!-- Agency Name Card -->
      <div class="card bg-white rounded shadow-sm p-6 border border-gray-100">
        <div class="flex items-center mb-4">
          <div class="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mr-3">
            <i class="ri-building-line text-primary"></i>
          </div>
          <h2 class="text-lg font-medium text-gray-700">Agency Name</h2>
        </div>
        <p class="text-2xl font-semibold text-gray-900">{{ (agencyName | titlecase) || '—' }}</p>
      </div>
      <!-- IATA Number Card -->
      <div class="card bg-white rounded shadow-sm p-6 border border-gray-100">
        <div class="flex items-center mb-4">
          <div class="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
            <i class="ri-passport-line text-blue-600"></i>
          </div>
          <h2 class="text-lg font-medium text-gray-700">Consumer Info</h2>
        </div>

        <div class="space-y-1">
          <div class="text-sm text-gray-500">IATA Number</div>
          <div class="text-2xl font-semibold text-gray-900">
            {{ consumerData?.iata_number || '—' }}
          </div>

          <div class="text-sm text-gray-500 mt-4">City Code</div>
          <div class="text-2xl font-semibold text-gray-900">
            {{ consumerData?.city_code || '—' }}
          </div>

          <div class="text-sm text-gray-500 mt-4">Currency Code</div>
          <div class="text-2xl font-semibold text-gray-900">
            {{ consumerData?.currency_code || '—' }}
          </div>
        </div>
      </div>
      <!-- Agency Manager Card -->
      <div class="card bg-white rounded shadow-sm p-6 border border-gray-100">
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center">
            <div class="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center mr-3">
              <i class="ri-user-star-line text-amber-600"></i>
            </div>
            <h2 class="text-lg font-medium text-gray-700">Agency Manager(s)</h2>
          </div>
          <button
            *ngIf="agentAccounts.length > 3"
            (click)="toggleShowAll()"
            class="p-1 rounded transition-all hover:bg-gray-100 hover:shadow-md group"
          >
            <i
              [ngClass]="{
                'ri-arrow-down-s-line': !showAllAgents,
                'ri-arrow-up-s-line': showAllAgents,
              }"
              class="inline-block text-gray-700 transition-transform duration-200 group-hover:scale-110"
            ></i>
          </button>
        </div>
        <div class="space-y-3">
          <p
            *ngIf="!agentAccounts || agentAccounts.length === 0"
            class="text-2xl font-semibold text-gray-900"
          >
            —
          </p>
          <div *ngIf="agentAccounts && agentAccounts.length > 0">
            <div *ngFor="let agent of visibleAgents" class="flex items-center space-x-3 mb-2">
              <div
                class="min-w-8 min-h-8 rounded-full bg-gray-100 flex items-center justify-center"
              >
                <i class="ri-user-line text-gray-600"></i>
              </div>
              <p class="text-gray-900 font-medium overflow-hidden text-ellipsis">
                {{ agent.email }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Second Row: Plan Allowance (superagency only) -->
    <div *ngIf="isSuperagency" class="card bg-white rounded shadow-sm border border-gray-100">
      <div class="p-6 border-b border-gray-100">
        <div class="flex items-center">
          <div class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mr-3">
            <i class="ri-bank-card-line text-gray-600"></i>
          </div>
          <h2
            class="w-fit text-lg font-medium text-gray-700 tooltip"
            [attr.data-tip]="
              'This table indicates the current agents and subagencies already in use, and the total number of agents and subagencies that you have included in your subscription.'
            "
          >
            Plan Allowance
            <i class="ri-information-line"></i>
          </h2>
        </div>
      </div>
      <div class="grid grid-cols-2 divide-x divide-gray-100">
        <div class="p-6">
          <div class="flex items-center mb-4">
            <div class="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-3">
              <i class="ri-team-line text-green-600"></i>
            </div>
            <span class="text-gray-700">Agents</span>
          </div>
          <div class="flex items-baseline gap-1">
            <span class="text-2xl font-semibold text-gray-900">{{ consumerData.agent_count }}</span>
            <span class="text-2xl text-gray-400">/</span>
            <span class="text-2xl font-semibold text-gray-900">
              {{ consumerData.agent_limit === 0 ? 'Unlimited' : consumerData.agent_limit }}
            </span>
          </div>
        </div>
        <div class="p-6">
          <div class="flex items-center mb-4">
            <div class="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mr-3">
              <i class="ri-store-2-line text-purple-600"></i>
            </div>
            <span class="text-gray-700">Subagencies</span>
          </div>
          <div class="flex items-baseline gap-1">
            <span class="text-2xl font-semibold text-gray-900">
              {{ consumerData.subagency_count }}
            </span>
            <span class="text-2xl text-gray-400">/</span>
            <span class="text-2xl font-semibold text-gray-900">
              {{ consumerData.subagency_enabled && (consumerData.subagency_limit === 0) ? 'Unlimited' : consumerData.subagency_limit }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <!-- Third Row -->
    <!-- Providers Enabled Card -->
    <div class="card bg-white rounded shadow-sm p-6 border border-gray-100">
      <div class="flex items-center mb-4">
        <div class="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center mr-3">
          <i class="ri-plane-line text-amber-600"></i>
        </div>
        <h2 class="text-lg font-medium text-gray-700">Providers Enabled</h2>
      </div>
      <div class="flex flex-wrap gap-2">
        <div
          *ngFor="let airline of airlines | keyvalue"
          class="airline-tag px-3 py-1.5 bg-gray-100 rounded-full text-sm font-medium text-gray-800"
        >
          {{ airline.key }}
          <span class="airline-tooltip">
            {{ airline.value.name }}
          </span>
        </div>
      </div>
    </div>
  </div>
  <!-- Action Buttons -->
  <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
    <button
      (click)="redirectToBP()"
      class="action-button !rounded-button bg-primary hover:bg-primary/90 text-white py-6 px-8 rounded-lg shadow-md flex items-center justify-center whitespace-nowrap"
    >
      <div class="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mr-4">
        <i class="ri-computer-line text-white ri-xl"></i>
      </div>
      <div class="text-left">
        <h3 class="text-xl font-semibold">Agent Desktop</h3>
        <p class="text-white/80 mt-1">Access the booking interface</p>
      </div>
    </button>
    <button
      (click)="redirectToSupport()"
      class="action-button !rounded-button bg-primary hover:bg-primary/90 text-white py-6 px-8 rounded-lg shadow-md flex items-center justify-center whitespace-nowrap"
    >
      <div class="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mr-4">
        <i class="ri-plug-line text-white ri-xl"></i>
      </div>
      <div class="text-left">
        <h3 class="text-xl font-semibold">AirGateway API</h3>
        <p class="text-white/80 mt-1">Manage API integration</p>
      </div>
    </button>
  </div>
</div>
